import qz, { type QzClient, type QzPrintData } from "qz-tray";
import { paperWidthMm } from "./document";
import { assertCommonPrintPreflight, createSingleUseSend } from "./preflight";
import { rasterizePrintDocument } from "./raster";
import { PrintProviderError, type PreparedPrint, type PrintJobLeaseContext, type PrintProvider, type PrintRequest } from "./types";

export type QzAllowlistedOperation = "PRINTERS_FIND" | "PRINT";
export type QzPrintDataFormat = "PIXEL_HTML" | "RAW_CASH_DRAWER" | "RAW_ESCPOS_RASTER";

/**
 * QZ supports pre-signed calls. The implementation behind this boundary sends
 * the complete call/params/timestamp envelope to the server so the signature
 * is bound to the inspected operation rather than an opaque caller digest.
 */
export interface QzSigningRequest {
  operation: QzAllowlistedOperation;
  payload: QzCallPayload;
  jobLease: PrintJobLeaseContext;
  queueId: string;
  copies?: number;
  dataFormat?: QzPrintDataFormat;
  authorizationToken: string;
}

export type QzAuthorizationRequest = Omit<QzSigningRequest, "payload" | "authorizationToken">;

export interface QzSecurityCallbacks {
  certificate(): Promise<string>;
  authorizeOperation(request: QzAuthorizationRequest): Promise<{ authorizationToken: string }>;
  authorize(request: QzSigningRequest): Promise<string>;
}

export interface QzProviderOptions {
  security: QzSecurityCallbacks;
  client?: QzClient;
  now?: () => Date;
}

export interface QzDiscoveryOptions {
  certificate(): Promise<string>;
  authorize(payload: QzCallPayload): Promise<string>;
  client?: QzClient;
}

export type QzCallPayload = {
  call: "printers.find" | "print";
  params: Record<string, unknown>;
  timestamp: number;
};

let qzOperationTail: Promise<void> = Promise.resolve();

/**
 * The signature for the ONE call about to be sent, made by our backend for
 * that exact payload.
 *
 * Why not QZ's "pre-signed" form (pass the signature to `print`/`find`): QZ
 * only declares the algorithm (`signAlgorithm`) on calls it signs through its
 * hook. A pre-signed call goes out without it, QZ Tray verifies it as SHA-1,
 * and a SHA-512 signature is refused as "Invalid Signature" — every print,
 * with no way to allow it. So each call passes our pre-computed timestamp and
 * no signature: QZ hashes the very `{call, params, timestamp}` our backend
 * inspected and signed, asks the hook, and sends `signAlgorithm: "SHA512"`.
 * Calls are serialized by `withQzOperation`, so one slot is enough.
 */
let pendingSignature: string | null = null;

/**
 * Backend-made signatures for calls QZ's public API cannot pre-sign
 * (`socket.open`/`close`, `networking.device` — the network scan), keyed by the
 * SHA-256 hex QZ hands the hook. The caller fixes the call's timestamp, so it
 * knows that digest before QZ asks; a digest nobody registered is refused.
 */
const signatureByDigest = new Map<string, string>();

export function registerQzSignature(digest: string, signature: string): void {
  signatureByDigest.set(digest, signature);
}

export function forgetQzSignature(digest: string): void {
  signatureByDigest.delete(digest);
}

/** Exported for the scan: the same trust and hook as printing. */
export function prepareQzSecurity(client: QzClient, certificate: () => Promise<string>): void {
  installSecurity(client, certificate);
}

export async function connectQz(client: QzClient): Promise<void> {
  await connect(client);
}

async function sendSigned<T>(signature: string, call: () => Promise<T>): Promise<T> {
  pendingSignature = signature;
  try {
    return await call();
  } finally {
    pendingSignature = null;
  }
}

async function withQzOperation<T>(action: () => Promise<T>): Promise<T> {
  const predecessor = qzOperationTail;
  let release!: () => void;
  qzOperationTail = new Promise<void>((resolve) => {
    release = resolve;
  });
  await predecessor;
  try {
    return await action();
  } finally {
    release();
  }
}

function installSecurity(client: QzClient, certificate: () => Promise<string>) {
  client.security.setCertificatePromise(certificate, { rejectOnFailure: true });
  client.security.setSignatureAlgorithm("SHA512");
  // Must stay an `async` function: QZ calls an AsyncFunction directly.
  client.security.setSignaturePromise(async (digest) => {
    const scanSignature = signatureByDigest.get(digest);
    if (scanSignature) {
      signatureByDigest.delete(digest);
      return scanSignature;
    }
    const signature = pendingSignature;
    pendingSignature = null;
    if (!signature) {
      // Nothing our backend signed is waiting: refuse rather than sign blindly.
      throw new PrintProviderError("QZ_AUTHORIZATION_FAILED", "Unsigned QZ callback operations are disabled.");
    }
    return signature;
  });
}

function printDataFor(request: PrintRequest): QzPrintData[] {
  const actions = request.hardwareActions;
  const commands = request.profile.rawCommands;
  const data: QzPrintData[] = request.documentHtml
    ? [
        {
          type: "pixel",
          format: "html",
          flavor: "plain",
          data: request.documentHtml,
          // QZ interprets this in the config's units (mm). Leave height automatic
          // for continuous receipt rolls.
          options: { pageWidth: paperWidthMm(request.paperWidth) },
        },
      ]
    : [];

  const append = (requested: boolean | undefined, command: string | undefined) => {
    if (!requested) return;
    if (!command) {
      throw new PrintProviderError("RAW_ACTION_UNSUPPORTED", "The requested raw action is disabled for this tested profile.");
    }
    data.push({
      type: "raw",
      format: "command",
      flavor: "base64",
      data: command,
    });
  };

  append(actions?.cut, commands?.cutBase64);
  append(actions?.openCashDrawer, commands?.cashDrawerBase64);
  return data;
}

async function connect(client: QzClient): Promise<void> {
  if (client.websocket.isActive()) return;
  try {
    await client.websocket.connect();
  } catch (firstFailure) {
    if (client.websocket.isActive()) return;
    try {
      await client.websocket.connect();
    } catch (secondFailure) {
      throw new PrintProviderError("QZ_CONNECTION_FAILED", "QZ Tray could not be reached.", secondFailure ?? firstFailure);
    }
  }
}

export function createQzPrintProvider(options: QzProviderOptions): PrintProvider {
  const client = options.client ?? qz;
  const now = options.now ?? (() => new Date());
  installSecurity(client, options.security.certificate);

  return {
    kind: "QZ",
    async preflight(request): Promise<PreparedPrint> {
      assertCommonPrintPreflight(request);
      if (!request.queueName?.trim()) {
        throw new PrintProviderError("QUEUE_REQUIRED", "An exact operating-system printer queue is required.");
      }
      if (!request.jobLease) {
        throw new PrintProviderError("QZ_JOB_CONTEXT_REQUIRED", "A leased print job is required for scoped QZ authorization.");
      }
      if (request.networkAddress) {
        return await preflightNetwork(client, options, now, request);
      }

      await connect(client);

      let queues: string[];
      try {
        const authorization = await options.security.authorizeOperation({
          operation: "PRINTERS_FIND",
          jobLease: request.jobLease,
          queueId: request.queueName,
        });
        const payload: QzCallPayload = {
          call: "printers.find",
          params: { query: request.queueName },
          timestamp: now().getTime(),
        };
        const signature = await options.security.authorize({
          operation: "PRINTERS_FIND",
          payload,
          jobLease: request.jobLease,
          queueId: request.queueName,
          authorizationToken: authorization.authorizationToken,
        });
        const found = await withQzOperation(() =>
          sendSigned(signature, () => client.printers.find(request.queueName, undefined, payload.timestamp)),
        );
        queues = Array.isArray(found) ? found : [found];
      } catch (cause) {
        throw new PrintProviderError(
          "QZ_AUTHORIZATION_FAILED",
          "Printer queues could not be read from QZ Tray.",
          cause,
        );
      }
      if (!queues.includes(request.queueName)) {
        throw new PrintProviderError("QUEUE_NOT_FOUND", "The exact configured operating-system printer queue was not found.");
      }

      const data = printDataFor(request);
      const width = paperWidthMm(request.paperWidth);
      const config = client.configs.create(request.queueName, {
        copies: request.copies,
        jobName: request.jobName,
        margins: 0,
        orientation: "portrait",
        rasterize: true,
        scaleContent: true,
        units: "mm",
        size: { width, custom: true },
      });

      return {
        provider: "QZ",
        dispose: () => undefined,
        send: createSingleUseSend(async ({ onBeforeDispatch }) => {
          let dispatchBoundaryCrossed = false;

          try {
            const authorization = await options.security.authorizeOperation({
              operation: "PRINT",
              jobLease: request.jobLease!,
              queueId: request.queueName!,
              copies: request.copies,
              dataFormat: request.hardwareActions?.openCashDrawer ? "RAW_CASH_DRAWER" : "PIXEL_HTML",
            });
            const payload: QzCallPayload = {
              call: "print",
              params: {
                printer: config.getPrinter(),
                options: config.getOptions(),
                data,
              },
              timestamp: now().getTime(),
            };
            let signature: string;
            try {
              signature = await options.security.authorize({
                operation: "PRINT",
                payload,
                jobLease: request.jobLease!,
                queueId: request.queueName!,
                copies: request.copies,
                dataFormat: request.hardwareActions?.openCashDrawer ? "RAW_CASH_DRAWER" : "PIXEL_HTML",
                authorizationToken: authorization.authorizationToken,
              });
            } catch (cause) {
              throw new PrintProviderError("QZ_AUTHORIZATION_FAILED", "The QZ request could not be authorized.", cause);
            }
            await onBeforeDispatch();
            dispatchBoundaryCrossed = true;
            await withQzOperation(() => sendSigned(signature, () => client.print(config, data, [undefined], payload.timestamp)));
            return {
              provider: "QZ",
              status: "SPOOLED",
              observedAt: now().toISOString(),
            };
          } catch (cause) {
            if (cause instanceof PrintProviderError && cause.code === "QZ_AUTHORIZATION_FAILED") {
              throw cause;
            }
            if (!dispatchBoundaryCrossed) {
              throw new PrintProviderError("QZ_DISPATCH_NOT_STARTED", "QZ rejected the request before printer actuation began.", cause);
            }
            return {
              provider: "QZ",
              status: "UNKNOWN",
              observedAt: now().toISOString(),
              errorClass: "QZ_SEND_OUTCOME_UNKNOWN",
            };
          }
        }),
      };
    },
  };
}

/**
 * `QZ_NETWORK`: no OS queue and no driver. Everything fallible — connecting to
 * QZ and drawing the raster — happens here, before the SENDING boundary; the
 * send step only authorizes, signs and dispatches (docs §7).
 */
async function preflightNetwork(
  client: QzClient,
  options: QzProviderOptions,
  now: () => Date,
  request: PrintRequest,
): Promise<PreparedPrint> {
  const address = request.networkAddress!;
  const jobLease = request.jobLease!;
  const queueId = request.queueName!;
  const openCashDrawer = Boolean(request.hardwareActions?.openCashDrawer);
  const dataFormat: QzPrintDataFormat = openCashDrawer ? "RAW_CASH_DRAWER" : "RAW_ESCPOS_RASTER";

  await connect(client);

  let command: string;
  if (openCashDrawer) {
    const pulse = request.profile.rawCommands?.cashDrawerBase64;
    if (!pulse) {
      throw new PrintProviderError("RAW_ACTION_UNSUPPORTED", "The requested raw action is disabled for this tested profile.");
    }
    command = pulse;
  } else {
    command = await rasterizePrintDocument({
      documentHtml: request.documentHtml!,
      paperWidth: request.paperWidth,
      cut: Boolean(request.hardwareActions?.cut),
    });
  }
  const data: QzPrintData[] = [{ type: "raw", format: "command", flavor: "base64", data: command }];
  const config = client.configs.create(
    { host: address.host, port: address.port },
    { copies: request.copies, jobName: request.jobName },
  );

  return {
    provider: "QZ",
    dispose: () => undefined,
    send: createSingleUseSend(async ({ onBeforeDispatch }) => {
      let dispatchBoundaryCrossed = false;
      try {
        const authorization = await options.security.authorizeOperation({
          operation: "PRINT",
          jobLease,
          queueId,
          copies: request.copies,
          dataFormat,
        });
        const payload: QzCallPayload = {
          call: "print",
          params: { printer: config.getPrinter(), options: config.getOptions(), data },
          timestamp: now().getTime(),
        };
        let signature: string;
        try {
          signature = await options.security.authorize({
            operation: "PRINT",
            payload,
            jobLease,
            queueId,
            copies: request.copies,
            dataFormat,
            authorizationToken: authorization.authorizationToken,
          });
        } catch (cause) {
          throw new PrintProviderError("QZ_AUTHORIZATION_FAILED", "The QZ request could not be authorized.", cause);
        }
        await onBeforeDispatch();
        dispatchBoundaryCrossed = true;
        await withQzOperation(() => sendSigned(signature, () => client.print(config, data, [undefined], payload.timestamp)));
        return { provider: "QZ", status: "SPOOLED", observedAt: now().toISOString() };
      } catch (cause) {
        if (cause instanceof PrintProviderError && cause.code === "QZ_AUTHORIZATION_FAILED") throw cause;
        if (!dispatchBoundaryCrossed) {
          throw new PrintProviderError("QZ_DISPATCH_NOT_STARTED", "QZ rejected the request before printer actuation began.", cause);
        }
        return { provider: "QZ", status: "UNKNOWN", observedAt: now().toISOString(), errorClass: "QZ_SEND_OUTCOME_UNKNOWN" };
      }
    }),
  };
}

/** Lists exact operating-system queues under a one-minute manager-issued session. */
export async function discoverQzPrinterQueues(options: QzDiscoveryOptions): Promise<string[]> {
  const client = options.client ?? qz;
  return withQzOperation(async () => {
    installSecurity(client, options.certificate);
    await connect(client);
    try {
      const payload: QzCallPayload = {
        call: "printers.find",
        params: {},
        timestamp: Date.now(),
      };
      const signature = await options.authorize(payload);
      const found = await sendSigned(signature, () => client.printers.find(undefined, undefined, payload.timestamp));
      return Array.isArray(found) ? found : [found];
    } catch (cause) {
      throw new PrintProviderError("QZ_AUTHORIZATION_FAILED", "Printer queues could not be read from QZ Tray.", cause);
    }
  });
}
